import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Form } from 'formik';
import { Col, Row } from 'reactstrap';
import * as PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import FormInputField from '../../common/components/formFields/FormInputField';
import CommonButton from '../../common/components/CommonButton';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';
import { clearValidationErrors, validateOrganization } from '../RegisterActions';
import CommonErrorLabel from '../../common/components/CommonErrorLabel';
import { validateFormField } from '../../common/helpers/validateFormField';
import { FormFieldTypes } from '../../constants/FormFieldTypes';
import { useMediaQuery } from '../../common/hooks/useMediaQuery';
import { scrollTop } from '../../common/helpers/generalHelper';
import locationIcon from '../../assets/img/locationicon.svg';

import phoneIcon from '../../assets/img/icons/phone.svg';
import webIcon from '../../assets/img/icons/web.svg';

const requiredFields = [
  'officialCompanyName',
  'tradeName',
  'vatNumber',
  'postalCode',
  'address',
  'city',
  'county',
  'phoneNumber',
  'mobileNumber',
];

function CompanyDetailsForm({ nextStep, values }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const validateErrors = useSelector((state) => state.register.get('validateErrors'));
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    scrollTop();
  }, []);

  const [errors, setErrors] = useState({
    officialCompanyNameError: '',
    tradeNameError: '',
    vatNumberError: '',
    postalCodeError: '',
    addressError: '',
    address2Error: '',
    cityError: '',
    countyError: '',
    phoneNumberError: '',
    mobileNumberError: '',
    websiteError: '',
    buyerNextGearIdError: '',
  });

  const clearValidationErrorsCallback = useCallback(
    () => dispatch(clearValidationErrors()),
    [dispatch],
  );

  useEffect(() => clearValidationErrorsCallback, [clearValidationErrorsCallback]);

  const validateForm = useCallback(
    () =>
      new Promise((resolve, reject) => {
        const {
          officialCompanyName,
          tradeName,
          vatNumber,
          postalCode,
          address,
          address2,
          city,
          county,
          phoneNumber,
          mobileNumber,
          website,
          buyerNextGearId,
        } = values;

        const formErrors = {
          officialCompanyNameError: validateFormField(
            officialCompanyName,
            FormFieldTypes.NAME,
            requiredFields.includes('officialCompanyName'),
            50,
          ),
          tradeNameError: validateFormField(
            tradeName,
            FormFieldTypes.NAME,
            requiredFields.includes('tradeName'),
            50,
          ),
          vatNumberError: validateFormField(
            vatNumber,
            FormFieldTypes.NUMBER,
            requiredFields.includes('vatNumber'),
            12,
            9,
          ),
          postalCodeError: validateFormField(
            postalCode,
            null,
            requiredFields.includes('postalCode'),
            8,
            6,
          ),
          addressError: validateFormField(
            address,
            null,
            requiredFields.includes('address'),
            100,
          ),
          address2Error: validateFormField(
            address2,
            null,
            requiredFields.includes('address2'),
            100,
          ),
          cityError: validateFormField(city, null, requiredFields.includes('city'), 100),
          countyError: validateFormField(county, null, requiredFields.includes('county'), 100),
          phoneNumberError: validateFormField(
            phoneNumber,
            FormFieldTypes.NUMBER,
            requiredFields.includes('phoneNumber'),
            15,
          ),
          mobileNumberError: validateFormField(
            mobileNumber,
            FormFieldTypes.NUMBER,
            requiredFields.includes('mobileNumber'),
            15,
          ),
          websiteError: validateFormField(
            website,
            FormFieldTypes.URL,
            requiredFields.includes('website'),
            200,
          ),
          buyerNextGearIdError: validateFormField(
            buyerNextGearId,
            null,
            requiredFields.includes('buyerNextGearId'),
            20,
          ),
        };

        setErrors(formErrors);

        if (Object.values(formErrors).some((error) => error)) {
          reject(formErrors);
        } else {
          resolve();
        }
      }),
    [values, setErrors],
  );

  useEffect(() => {
    if (submitted) {
      validateForm().catch(() => {});
    }
  }, [values, submitted, validateForm]);

  const moveToNextStep = useCallback(() => {
    setSubmitted(true);
    validateForm()
      .then(async () => {
        dispatch(validateOrganization(values, () => nextStep()));
      })
      .catch((errs) => {
        if (isMobile) {
          const firstErrorField = Object.keys(errs).find((key) => errs[key]);
          if (firstErrorField) {
            const fieldName = firstErrorField.replace('Error', '');
            const element = document.getElementsByName(fieldName)[0];
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }
      });
  }, [validateForm, dispatch, values, nextStep, isMobile]);

  const {
    officialCompanyNameError,
    tradeNameError,
    vatNumberError,
    postalCodeError,
    addressError,
    address2Error,
    cityError,
    countyError,
    mobileNumberError,
    phoneNumberError,
    websiteError,
    buyerNextGearIdError,
  } = errors;

  const headerStyle = useMemo(() => ({ borderBottom: '2px solid #F1001E' }), []);

  return (
    <Col
      md="12"
      className="register-form-container d-flex flex-column justify-content-center align-items-center"
    >
      <Row md={12} className="register-form-container__title w-100">
        <Col md={12}>
          <h2 className="font-weight-bold" style={headerStyle}>
            Company details
          </h2>
        </Col>
      </Row>
      <Row md={12} className="register-form-container__content justify-content-center w-100">
        <Form autoComplete="off" className="register-form__company-details">
          <Row className="justify-content-between mt-5 mb-3">
            <Col md="8">
              <div className="p-1 mobile-margin">
                <FormInputField
                  name="officialCompanyName"
                  label="Company Name"
                  invalid={!!officialCompanyNameError}
                  labelVariant="bordered"
                  required
                />
                <CommonErrorLabel value={officialCompanyNameError} />
              </div>
            </Col>
            <Col md="4">
              <div className="p-1">
                <FormInputField
                  name="vatNumber"
                  label="VAT Registration Number"
                  invalid={!!vatNumberError || validateErrors?.addOrganizationAndRoleForm}
                  required
                  labelVariant="bordered"
                />
                <CommonErrorLabel
                  value={vatNumberError || validateErrors?.addOrganizationAndRoleForm}
                />
              </div>
            </Col>
          </Row>
          <Row className="justify-content-between">
            <Col md="8 d-flex justify-content-center ">
              <div className="p-1 w-100">
                <FormInputField
                  name="tradeName"
                  label="Trade Name"
                  invalid={!!tradeNameError}
                  labelVariant="bordered"
                  required
                />
                <CommonErrorLabel value={tradeNameError} />
              </div>
            </Col>
            <Col md="4 d-flex justify-content-center ">
              <div className="p-1 w-100">
                <FormInputField
                  name="buyerNextGearId"
                  label="Buyer NextGear ID"
                  invalid={!!buyerNextGearIdError || validateErrors?.buyerNextGearId}
                  labelVariant="bordered"
                />
                <CommonErrorLabel
                  value={buyerNextGearIdError || validateErrors?.buyerNextGearId}
                />
              </div>
            </Col>
          </Row>
        </Form>
      </Row>

      <Row md={12} className="register-form-container__title w-100">
        <Col md={12}>
          <h2 className="font-weight-bold" style={headerStyle}>
            Company address
          </h2>
        </Col>
      </Row>
      <Row
        md={12}
        className="register-form-container__content justify-content-center w-100 mt-5"
      >
        <Form autoComplete="off" className="register-form__address-details">
          <Row className="justify-content-between mb-3">
            <Col md="6">
              <div className="p-1 mobile-margin">
                <FormInputField
                  name="address"
                  label="Address"
                  invalid={!!addressError}
                  labelVariant="bordered"
                  required
                />
                <CommonErrorLabel value={addressError} />
              </div>
            </Col>
            <Col md="6">
              <div className="p-1">
                <FormInputField
                  name="address2"
                  label="Address - Line 2"
                  invalid={!!address2Error}
                  labelVariant="bordered"
                />
                <CommonErrorLabel value={address2Error} />
              </div>
            </Col>
          </Row>
          <Row className="justify-content-between mb-3">
            <Col md="4">
              <div className="p-1 mobile-margin">
                <FormInputField
                  name="city"
                  label="Town"
                  invalid={!!cityError}
                  labelVariant="bordered"
                  icon={locationIcon}
                  iconClassName="input-right-icon"
                  required
                />
                <CommonErrorLabel value={cityError} />
              </div>
            </Col>
            <Col md="4">
              <div className="p-1 mobile-margin">
                <FormInputField
                  name="postalCode"
                  label="Post Code"
                  upperCase
                  invalid={!!postalCodeError}
                  labelVariant="bordered"
                  capitalize
                  required
                />
                <CommonErrorLabel value={postalCodeError} />
              </div>
            </Col>
            <Col md="4">
              <div className="p-1">
                <FormInputField
                  name="county"
                  label="County"
                  invalid={!!countyError}
                  labelVariant="bordered"
                  required
                />
                <CommonErrorLabel value={countyError} />
              </div>
            </Col>
          </Row>
          <Row className="justify-content-between mb-3">
            <Col md="4">
              <div className="p-1 mobile-margin">
                <FormInputField
                  name="mobileNumber"
                  label="Mobile Number"
                  invalid={!!mobileNumberError}
                  icon={phoneIcon}
                  iconClassName="input-right-icon phone-icon"
                  labelVariant="bordered"
                  required
                />
                <CommonErrorLabel value={mobileNumberError} />
              </div>
            </Col>
            <Col md="4">
              <div className="p-1 mobile-margin">
                <FormInputField
                  name="phoneNumber"
                  label="Landline Number"
                  invalid={!!phoneNumberError}
                  icon={phoneIcon}
                  iconClassName="input-right-icon phone-icon"
                  labelVariant="bordered"
                  required
                />
                <CommonErrorLabel value={phoneNumberError} />
              </div>
            </Col>
            <Col md="4">
              <div className="p-1">
                <FormInputField
                  name="website"
                  label="Website Address"
                  invalid={!!websiteError}
                  icon={webIcon}
                  iconClassName="input-right-icon"
                  labelVariant="bordered"
                />
                <CommonErrorLabel value={websiteError} />
              </div>
            </Col>
          </Row>
        </Form>
      </Row>

      <Row className="w-100 justify-content-end mt-4">
        <Col xs="6" sm="6" md="6" lg="6" xl="6">
          <CommonButton
            label="NEXT"
            variant={CommonButtonVariants.PRIMARY_RED}
            handleClick={moveToNextStep}
            className="w-100"
          />
        </Col>
      </Row>
    </Col>
  );
}

CompanyDetailsForm.propTypes = {
  nextStep: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
};

export default CompanyDetailsForm;
